import React, { useEffect, useState } from "react";
import {
	Box,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	CircularProgress,
	Alert,
	Chip,
	Button,
	Card,
	CardContent,
	Grid,
	Tabs,
	Tab,
	TextField,
	MenuItem,
} from "@mui/material";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { getAiDrinks } from "../../../../services/Admin/AiDrinksApi";
import { getDrinkRequests } from "../../../../services/Admin/NamedDrinksRequestApi";
import DrinkDetailsDialog from "./DrinkDetailsDialog";
import UpdateDrinkDataDialog from "./UpdateDrinkDataDialog";
import AllAiDrinkRequests from "./AiDrinkRequests/AllAiDrinkRequests";
import { useAuth } from "../../../../Context/AuthContext";


const TAB_STORAGE_KEY = "clint-ai-drinks-active-tab";

const getTodayDateValue = () => {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const ClintAiDrinksPage = () => {
	const [tabValue, setTabValue] = useState(() => {
		const savedTab = localStorage.getItem(TAB_STORAGE_KEY);
		const parsedTab = Number(savedTab);
		return parsedTab === 0 || parsedTab === 1 ? parsedTab : 0;
	});
	const [drinks, setDrinks] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
	const [selectedDrink, setSelectedDrink] = useState(null);
	const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
	const [drinkToUpdate, setDrinkToUpdate] = useState(null);
	const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
	const [dateFrom, setDateFrom] = useState(getTodayDateValue);
	const [dateTo, setDateTo] = useState(getTodayDateValue);
	const [menuVisibilityFilter, setMenuVisibilityFilter] = useState("all");
	const [approvalFilter, setApprovalFilter] = useState("1");
	const [statusFilter, setStatusFilter] = useState("all");
	const [searchTerm, setSearchTerm] = useState("");
	const { hasPermission } = useAuth();

	const handleTabChange = (event, newValue) => {
		setTabValue(newValue);
	};

	useEffect(() => {
		localStorage.setItem(TAB_STORAGE_KEY, String(tabValue));
	}, [tabValue]);

	useEffect(() => {
		fetchAiDrinks();
		fetchPendingRequestsCount();
	}, []);

	const fetchAiDrinks = async () => {
		setLoading(true);
		setError("");

		try {
			const response = await getAiDrinks();
			setDrinks(response.data || []);
		} catch (err) {
			console.error("خطأ في جلب مشروبات العملاء:", err);
			setError(err.message || "حدث خطأ في جلب مشروبات العملاء");
		} finally {
			setLoading(false);
		}
	};

	const fetchPendingRequestsCount = async () => {
		try {
			const response = await getDrinkRequests();
			const requests = response?.data || [];
			const pendingOnly = requests.filter((req) => req?.status === "pending");
			setPendingRequestsCount(pendingOnly.length);
		} catch (err) {
			console.error("خطأ في جلب طلبات تسمية المشروبات:", err);
			setPendingRequestsCount(0);
		}
	};

	const getApprovalChip = (drink) => {
		if (drink.is_approved === 1) {
			return (
				<Chip
					icon={<CheckCircleIcon />}
					label="معتمد"
					size="small"
					color="success"
				/>
			);
		}

		if (drink.is_approved === 0) {
			return (
				<Chip
					icon={<HourglassTopIcon />}
					label=" لم يقبلة العميل"
					size="small"
					color="warning"
				/>
			);
		}

		return (
			<Chip
				icon={<CancelIcon />}
				label="غير معتمد"
				size="small"
				color="error"
			/>
		);
	};

	const getPublicChip = (drink) => {
		if (Number(drink?.is_public) === 1) {
			return <Chip label="معروض في المنيو " size="small" color="success" />;
		}

		return <Chip label="غير معروض " size="small" color="default" variant="outlined" />;
	};

	const getStatusChip = (drink) => {
		if (Number(drink?.status) === 1) {
			return <Chip label="نشط" size="small" color="success" />;
		}

		return <Chip label="غير نشط" size="small" color="default" variant="outlined" />;
	};

	const handleRowClick = (drink) => {
    if (hasPermission("view")) { // استبدل "view_drink_details" باسم الصلاحية المناسبة
        setSelectedDrink(drink);
        setDetailsDialogOpen(true);
    } else {
        alert("ليس لديك الصلاحية لعرض تفاصيل هذا المشروب");
        // ممكن تستخدم Snackbar بدل alert لو عايز تصميم أفضل
    }
};

	const handleCloseDetailsDialog = () => {
		setDetailsDialogOpen(false);
		setSelectedDrink(null);
	};

	const handleEditDrink = (drink) => {
		setDrinkToUpdate(drink);
		setUpdateDialogOpen(true);
		setDetailsDialogOpen(false);
	};

	const handleCloseUpdateDialog = () => {
		setUpdateDialogOpen(false);
		setDrinkToUpdate(null);
	};

	const handleDrinkUpdated = () => {
		fetchAiDrinks();
		fetchPendingRequestsCount();
	};

	const renderIngredients = (drink) => {
		if (Array.isArray(drink.materials) && drink.materials.length > 0) {
			return (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
					{drink.materials.slice(0, 3).map((item) => (
						<Typography key={item.id} variant="caption" color="text.secondary">
							• {item.material?.name || `مادة #${item.material_id}`} - {item.quantity}
							{" "}
							{item.material?.measurment_unit?.abbreviation || ""}
						</Typography>
					))}
					{drink.materials.length > 3 && (
						<Typography variant="caption" color="primary.main">
							+{drink.materials.length - 3} مكونات إضافية
						</Typography>
					)}
				</Box>
			);
		}

		if (!drink.ingredients) return <Typography variant="caption">-</Typography>;

		try {
			const parsed = JSON.parse(drink.ingredients);
			if (Array.isArray(parsed) && parsed.length > 0) {
				return (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
						{parsed.slice(0, 3).map((item, idx) => (
							<Typography key={idx} variant="caption" color="text.secondary">
								• {item.name || `مادة #${item.material_id}`} - {item.amount} {item.unit}
							</Typography>
						))}
						{parsed.length > 3 && (
							<Typography variant="caption" color="primary.main">
								+{parsed.length - 3} مكونات إضافية
							</Typography>
						)}
					</Box>
				);
			}
			return <Typography variant="caption">-</Typography>;
		} catch {
			return <Typography variant="caption">-</Typography>;
		}
	};

	const renderRatingsColumn = (drink) => {
		const ratings = Array.isArray(drink?.ratings) ? drink.ratings : [];

		if (ratings.length === 0) {
			return <Typography variant="caption">لا توجد تقييمات</Typography>;
		}

		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
				{ratings.slice(0, 2).map((rating, idx) => {
					const createdAt = rating?.created_at ? new Date(rating.created_at) : null;
					const safeDate =
						createdAt && !Number.isNaN(createdAt.getTime())
							? createdAt.toLocaleString("ar-EG", {
								year: "numeric",
								month: "short",
								day: "numeric",
								hour: "2-digit",
								minute: "2-digit",
								hour12: true,
							})
							: "-";

					return (
						<Box key={rating?.id || idx} sx={{ p: 0.75, borderRadius: 1, bgcolor: "grey.100" }}>
							<Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
								الدرجة: {Number(rating?.rate || 0)} / 5
							</Typography>
							<Typography variant="caption" sx={{ display: "block" }}>
								التعليق: {rating?.comment || "بدون تعليق"}
							</Typography>
							<Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
								المستخدم: {rating?.user?.name || "-"}
							</Typography>
							<Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
								رقم الهاتف: {rating?.user?.phone || "-"}
							</Typography>
							<Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
								التاريخ: {safeDate}
							</Typography>
						</Box>
					);
				})}

				{ratings.length > 2 && (
					<Typography variant="caption" color="primary.main">
						+{ratings.length - 2} تقييم إضافي
					</Typography>
				)}
			</Box>
		);
	};

	const renderCreatorColumn = (drink) => {
		const creator = drink?.creator;

		if (!creator) {
			return <Typography variant="caption">-</Typography>;
		}

		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
				<Typography variant="body2" sx={{ fontWeight: 700 }}>
					{creator?.name || "-"}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{creator?.phone || "-"}
				</Typography>
			</Box>
		);
	};

	const getIngredientsPlain = (drink) => {
		if (Array.isArray(drink?.materials) && drink.materials.length > 0) {
			return drink.materials
				.map((item) => {
					const name = item?.material?.name || `مادة #${item?.material_id || "-"}`;
					const qty = item?.quantity || "-";
					const unit = item?.material?.measurment_unit?.abbreviation || "";
					return `${name}: ${qty} ${unit}`.trim();
				})
				.join(" | ");
		}

		if (!drink?.ingredients) return "-";

		try {
			const parsed = JSON.parse(drink.ingredients);
			if (!Array.isArray(parsed)) return "-";
			return parsed
				.map((item) => `${item?.name || `مادة #${item?.material_id || "-"}`}: ${item?.amount || "-"} ${item?.unit || ""}`.trim())
				.join(" | ");
		} catch {
			return "-";
		}
	};

	const getRatingsPlain = (drink) => {
		const ratings = Array.isArray(drink?.ratings) ? drink.ratings : [];
		if (ratings.length === 0) return "لا توجد تقييمات";

		return ratings
			.map((rating) => {
				const userName = rating?.user?.name || "-";
				const userPhone = rating?.user?.phone || "-";
				const rate = Number(rating?.rate || 0);
				const comment = rating?.comment || "بدون تعليق";
				const date = rating?.created_at || "-";
				return `درجة: ${rate}/5, تعليق: ${comment}, المستخدم: ${userName}, الهاتف: ${userPhone}, التاريخ: ${date}`;
			})
			.join(" || ");
	};

	const getSearchableText = (drink) => {
		const materialsText = Array.isArray(drink?.materials)
			? drink.materials
				.map((item) => [
					item?.material?.name,
					item?.material?.name_en,
					item?.quantity,
					item?.material?.measurment_unit?.abbreviation,
					item?.material?.measurment_unit?.name,
				].filter(Boolean).join(" "))
				.join(" ")
			: "";

		const ratingsText = Array.isArray(drink?.ratings)
			? drink.ratings
				.map((rating) => [
					rating?.rate,
					rating?.comment,
					rating?.created_at,
					rating?.user?.name,
					rating?.user?.phone,
				].filter(Boolean).join(" "))
				.join(" ")
			: "";

		const ingredientsText = getIngredientsPlain(drink);

		return [
			drink?.id,
			drink?.drink_name,
			drink?.name,
			drink?.name_en,
			drink?.code,
			drink?.description,
			drink?.description_en,
			drink?.taste,
			drink?.temperature,
			drink?.approval_notes,
			drink?.status,
			Number(drink?.status) === 1 ? "نشط" : "غير نشط",
			drink?.price,
			drink?.prepare_time,
			drink?.created_at,
			drink?.updated_at,
			drink?.category?.name,
			drink?.category?.name_en,
			Number(drink?.is_public) === 1 ? "معروض في المنيو" : "غير معروض",
			Number(drink?.is_approved) === 1 ? "معتمد" : " لم يقبلة العميل",
			materialsText,
			ingredientsText,
			ratingsText,
		]
			.filter((value) => value !== undefined && value !== null)
			.join(" ")
			.toLowerCase();
	};

	const exportFilteredDataToExcel = () => {
		const headers = [
			"رقم المشروب",
			"اسم المشروب",
			"الاسم",
			"الاسم الانجليزي",
			"الفئة",
			"السعر",
			"وقت التحضير",
			"الحرارة",
			"الاعتماد",
			"في المنيو",
			"تاريخ الانشاء",
			"المكونات",
			"التقييمات",
		];

		headers.splice(9, 0, "الحالة");

		const escapeCsv = (value) => {
			const safe = String(value ?? "").replace(/"/g, '""');
			return `"${safe}"`;
		};

		const rows = filteredDrinks.map((drink) => [
			drink?.id || "",
			drink?.drink_name || "",
			drink?.name || "",
			drink?.name_en || "",
			drink?.category?.name || "غير محدد",
			drink?.price || "0.00",
			drink?.prepare_time || 0,
			drink?.temperature || "-",
			drink?.is_approved === 1 ? "معتمد" : " لم يقبلة العميل",
			Number(drink?.is_public) === 1 ? "1" : "0",
			drink?.created_at || "",
			getIngredientsPlain(drink),
			getRatingsPlain(drink),
		]);

		rows.forEach((row, index) => {
			const drink = filteredDrinks[index];
			row.splice(9, 0, Number(drink?.status) === 1 ? "نشط" : "غير نشط");
		});

		const csvContent = [headers, ...rows]
			.map((row) => row.map(escapeCsv).join(","))
			.join("\n");

		const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		const today = getTodayDateValue();
		link.href = url;
		link.download = `ai-drinks-report-${today}.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	if (loading && tabValue === 0) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "400px",
				}}
			>
				<CircularProgress size={60} />
			</Box>
		);
	}

	const filteredDrinks = drinks.filter((drink) => {
		if (!drink?.created_at) return false;

		const createdAt = new Date(drink.created_at);
		if (Number.isNaN(createdAt.getTime())) return false;

		if (dateFrom) {
			const fromDate = new Date(`${dateFrom}T00:00:00`);
			if (createdAt < fromDate) return false;
		}

		if (dateTo) {
			const toDate = new Date(`${dateTo}T23:59:59.999`);
			if (createdAt > toDate) return false;
		}

		if (menuVisibilityFilter === "1" && Number(drink?.is_public) !== 1) return false;
		if (menuVisibilityFilter === "0" && Number(drink?.is_public) !== 0) return false;
		if (approvalFilter === "1" && Number(drink?.is_approved) !== 1) return false;
		if (approvalFilter === "0" && Number(drink?.is_approved) !== 0) return false;
		if (
			approvalFilter === "-1" &&
			(Number(drink?.is_approved) === 1 || Number(drink?.is_approved) === 0)
		) return false;
		if (statusFilter === "1" && Number(drink?.status) !== 1) return false;
		if (statusFilter === "0" && Number(drink?.status) === 1) return false;

		if (searchTerm.trim()) {
			const q = searchTerm.trim().toLowerCase();
			const searchable = getSearchableText(drink);
			if (!searchable.includes(q)) return false;
		}

		return true;
	});

	const approvedCount = filteredDrinks.filter((d) => d.is_approved === 1).length;
	const pendingCount = filteredDrinks.filter((d) => d.is_approved === 0).length;

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
				<LocalCafeIcon sx={{ fontSize: 40, color: "primary.main" }} />
				<Typography variant="h4" sx={{ fontWeight: "bold" }}>
					مشروبات العملاء (AI)
				</Typography>
			</Box>

			{/* Tabs */}
			<Paper sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
				<Tabs value={tabValue} onChange={handleTabChange} dir="rtl">
					<Tab
						label="المشروبات المقترحة"
						icon={<LocalCafeIcon />}
						iconPosition="start"
					/>
					<Tab
						label={`طلبات العملاء (${pendingRequestsCount})`}
						icon={<AssignmentIcon />}
						iconPosition="start"
					/>
				</Tabs>
			</Paper>

			{/* Tab 0: المشروبات المقترحة */}
			{tabValue === 0 && (
				<Box>
					{error && (
						<Alert severity="error" sx={{ mb: 3 }}>
							{error}
						</Alert>
					)}

					{filteredDrinks.length > 0 && (
				<Grid container spacing={2} sx={{ mb: 3 }}>
					<Grid item xs={12} md={4}>
						<Card sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
							<CardContent>
								<Typography variant="h6" sx={{ color: "white", mb: 1 }}>
									إجمالي المشروبات
								</Typography>
								<Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
									{filteredDrinks.length}
								</Typography>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} md={4}>
						<Card sx={{ background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" }}>
							<CardContent>
								<Typography variant="h6" sx={{ color: "white", mb: 1 }}>
									المشروبات المعتمدة
								</Typography>
								<Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
									{approvedCount}
								</Typography>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} md={4}>
						<Card sx={{ background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" }}>
							<CardContent>
								<Typography variant="h6" sx={{ color: "white", mb: 1 }}>
									 لم يقبلة العميل
								</Typography>
								<Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
									{pendingCount}
								</Typography>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			)}

					<Paper sx={{ p: 2, mb: 2 }}>
						<Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
							<TextField
								label="بحث شامل"
								size="small"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder="ابحث في كل البيانات: الاسم، الكود، الوصف، المكونات، التقييمات، اسم/هاتف المستخدم..."
								sx={{ minWidth: 360, flex: 1 }}
							/>
							<TextField
								label="من تاريخ الإنشاء"
								type="date"
								size="small"
								value={dateFrom}
								onChange={(e) => setDateFrom(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
							<TextField
								label="إلى تاريخ الإنشاء"
								type="date"
								size="small"
								value={dateTo}
								onChange={(e) => setDateTo(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
							<TextField
								label="في المنيو؟"
								select
								size="small"
								value={menuVisibilityFilter}
								onChange={(e) => setMenuVisibilityFilter(e.target.value)}
								sx={{ minWidth: 150 }}
							>
								<MenuItem value="all">الكل</MenuItem>
								<MenuItem value="1">معروض (1)</MenuItem>
								<MenuItem value="0">غير معروض (0)</MenuItem>
							</TextField>
							<TextField
								label="الاعتماد"
								select
								size="small"
								value={approvalFilter}
								onChange={(e) => setApprovalFilter(e.target.value)}
								sx={{ minWidth: 150 }}
							>
								<MenuItem value="all">الكل</MenuItem>
								<MenuItem value="1">معتمد</MenuItem>
								<MenuItem value="0"> لم يقبلة العميل</MenuItem>
								<MenuItem value="-1">غير معتمد</MenuItem>
							</TextField>
							<TextField
								label="الحالة"
								select
								size="small"
								value={statusFilter}
								onChange={(e) => setStatusFilter(e.target.value)}
								sx={{ minWidth: 150 }}
							>
								<MenuItem value="all">الكل</MenuItem>
								<MenuItem value="1">نشط</MenuItem>
								<MenuItem value="0">غير نشط</MenuItem>
							</TextField>
							{hasPermission("export") && (
							<Button
								variant="contained"
								size="small"
								onClick={exportFilteredDataToExcel}
								disabled={filteredDrinks.length === 0}
							>
								تصدير Excel
							</Button>
							)}

						</Box>
					</Paper>

					{drinks.length === 0 ? (
						<Paper sx={{ p: 4, textAlign: "center" }}>
							<LocalCafeIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
							<Typography variant="h6" color="text.secondary">
								لا توجد مشروبات منشأة من العملاء
							</Typography>
						</Paper>
					) : filteredDrinks.length === 0 ? (
						<Paper sx={{ p: 4, textAlign: "center" }}>
							<Typography variant="h6" color="text.secondary">
								لا توجد مشروبات ضمن نطاق التاريخ المحدد
							</Typography>
						</Paper>
					) : (
						<TableContainer component={Paper} sx={{ boxShadow: 3 }}>
							<Table dir="rtl">
								<TableHead>
									<TableRow sx={{ backgroundColor: "primary.main" }}>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											#
										</TableCell>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											اسم المشروب
										</TableCell>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											الاسم
										</TableCell>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											منشئ المشروب
										</TableCell>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											الفئة
										</TableCell>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											السعر / التحضير
										</TableCell>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											الحرارة
										</TableCell>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											المكونات
										</TableCell>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											الاعتماد
										</TableCell>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											في المنيو؟
										</TableCell>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											التقييمات
										</TableCell>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											تاريخ الإنشاء
										</TableCell>
										<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
											الحالة
										</TableCell>
									</TableRow>
								</TableHead>

								<TableBody>
									{filteredDrinks.map((drink, index) => (
										<TableRow
											key={drink.id}
											onClick={() => handleRowClick(drink)}
											sx={{
												"&:hover": { backgroundColor: "action.hover", cursor: "pointer" },
												backgroundColor: index % 2 === 0 ? "background.default" : "background.paper",
											}}
										>
											<TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
												{drink.id}
											</TableCell>

											<TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
												{drink.drink_name || "-"}
											</TableCell>

											<TableCell sx={{ textAlign: "right" }}>
												<Box sx={{ display: "flex", flexDirection: "column" }}>
													<Typography variant="body2">{drink.name || "-"}</Typography>
													<Typography variant="caption" color="text.secondary">
														{drink.name_en || "-"}
													</Typography>
												</Box>
											</TableCell>

											<TableCell sx={{ textAlign: "right", minWidth: 170 }}>
												{renderCreatorColumn(drink)}
											</TableCell>

											<TableCell sx={{ textAlign: "right" }}>
												{drink.category?.name || "غير محدد"}
											</TableCell>

											<TableCell sx={{ textAlign: "right" }}>
												<Box sx={{ display: "flex", flexDirection: "column" }}>
													<Typography variant="body2">{drink.price || "0.00"}</Typography>
													<Typography variant="caption" color="text.secondary">
														{drink.prepare_time || 0} دقيقة
													</Typography>
												</Box>
											</TableCell>

											<TableCell sx={{ textAlign: "right" }}>
												{drink.temperature || "-"}
											</TableCell>

											<TableCell sx={{ textAlign: "right", minWidth: 220 }}>
												{renderIngredients(drink)}
											</TableCell>

											<TableCell sx={{ textAlign: "right" }}>
												{getApprovalChip(drink)}
											</TableCell>

											<TableCell sx={{ textAlign: "right" }}>
												{getPublicChip(drink)}
											</TableCell>

											<TableCell sx={{ textAlign: "right", minWidth: 240 }}>
												{renderRatingsColumn(drink)}
											</TableCell>

											<TableCell sx={{ textAlign: "right" }}>
												<Typography variant="body2">
													{new Date(drink.created_at).toLocaleDateString("ar-EG", {
														year: "numeric",
														month: "short",
														day: "numeric",
													})}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{new Date(drink.created_at).toLocaleTimeString("ar-EG", {
														hour: "2-digit",
														minute: "2-digit",
														hour12: true,
													})}
												</Typography>
											</TableCell>
											<TableCell sx={{ textAlign: "right" }}>
												{getStatusChip(drink)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Box>
			)}

			{/* Tab 1: طلبات العملاء */}
			{tabValue === 1 && (
				<AllAiDrinkRequests />
			)}

			{tabValue === 0 && (
				<>
					<DrinkDetailsDialog
						open={detailsDialogOpen}
						onClose={handleCloseDetailsDialog}
						drink={selectedDrink}
						onEdit={handleEditDrink}
					/>

					<UpdateDrinkDataDialog
						open={updateDialogOpen}
						onClose={handleCloseUpdateDialog}
						drink={drinkToUpdate}
						onUpdated={handleDrinkUpdated}
					/>
				</>
			)}
		</Box>
	);
};

export default ClintAiDrinksPage;
